import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import {
  FaMapMarkerAlt,
  FaCheck,
  FaRegHeart,
  FaRegCommentAlt,
  FaShare,
  FaPhone,
  FaCalendarAlt,
  FaDownload,
  FaEnvelope,
  FaEye,
  FaCopy,
  FaUser,
  FaShieldAlt,
  FaUserFriends,
} from 'react-icons/fa';
import WebsiteTopBar from '../../components/WebsiteTopBar';
import {
  PublicListingSpecTabContent,
  PUBLIC_SPEC_TAB_IDS,
  getPublicSpecTabLabel,
} from '../../components/infrastructureLeasing/PublicListingSpecContent';
import InfrastructureLeasingSendEnquiryModal from '../../components/infrastructureLeasing/InfrastructureLeasingSendEnquiryModal';
import InfrastructureLeasingViewEnquiryModal from '../../components/infrastructureLeasing/InfrastructureLeasingViewEnquiryModal';
import StorageService from '../../services/StorageService';
import { fetchPublishedInfrastructureById } from '../../services/infrastructureLeasingListing.service';
import { resolvePublicFileUrl } from '../../constants/api-urls';
import {
  formatBookedRangesSummaryLines,
  estimateEarliestCapacityAfterBookings,
  getInventoryLineSummaryBullets,
  getListingAvailabilityHeadline,
} from '../../utils/infrastructureListingInventoryAvailabilityUi';
import 'bootstrap/dist/css/bootstrap.min.css';
import '../../assets/styles/website-top-bar.scss';
import '../../assets/styles/infrastructure_listing_public_detail.scss';

const PROPERTY_TYPE_LABELS = {
  office_space: 'Office Space',
  retail_shop: 'Retail Shop',
  warehouse: 'Warehouse',
  factory: 'Factory',
  industrial_shed: 'Industrial Shed',
  coworking_space: 'Coworking',
  land: 'Land',
  cold_storage: 'Cold Storage',
  agricultural_land: 'Agricultural',
  hospitality_property: 'Hospitality',
};

function str(v) {
  if (v == null) return '';
  const s = String(v).trim();
  return s;
}

function formatDeposit(raw) {
  if (raw == null || raw === '') return null;
  const n = Number(String(raw).replace(/,/g, ''));
  if (Number.isFinite(n) && n >= 0) {
    if (n >= 100000) {
      const l = n / 100000;
      const t = l % 1 === 0 ? l.toFixed(0) : l.toFixed(2);
      return `₹${t} Lakhs`;
    }
    return `₹${n.toLocaleString('en-IN')}`;
  }
  return str(raw) || null;
}

function perSqftLine(rentAmount, carpetStr) {
  if (rentAmount == null || !Number.isFinite(rentAmount) || rentAmount < 0) return null;
  const sq = Number(String(carpetStr || '').replace(/,/g, ''));
  if (!Number.isFinite(sq) || sq <= 0) return null;
  const p = rentAmount / sq;
  if (!Number.isFinite(p) || p <= 0) return null;
  return `₹${p.toFixed(0)} per sq ft`;
}

function pricePeriodLabel(form) {
  if (!form || typeof form !== 'object') return 'per month';
  const pm = str(form.pricingModel);
  if (pm === 'daily_short_rental') return 'per day';
  if (pm === 'hourly_very_short') return 'per hour';
  return 'per month';
}

/**
 * Large headline amount (e.g. ₹4.50 Lakhs) + period from API / rent.
 */
/** Placeholder engagement line until listing analytics are wired (consistent per id). */
function seedEngagementFromListingId(id) {
  const n = parseInt(String(id), 10) || 0;
  if (!n) {
    return { views: 1876, saves: 95, enquiries: 34 };
  }
  const views = 600 + (n * 19) % 5000;
  const saves = 20 + (n * 5) % 200;
  const enquiries = 8 + (n * 3) % 100;
  return { views, saves, enquiries };
}

function formatHeaderPriceAmount(listing) {
  const n = listing?.rentAmount;
  if (n != null && Number.isFinite(n) && n >= 0) {
    if (n >= 100000) {
      const lakhs = n / 100000;
      const t = lakhs % 1 === 0 ? lakhs.toFixed(0) : Number(lakhs.toFixed(2));
      return `₹${t} Lakhs`;
    }
    if (n >= 1000) {
      return `₹${(n / 1000).toFixed(2).replace(/\.?0+$/, '')}K`;
    }
    return `₹${n.toLocaleString('en-IN')}`;
  }
  const pp = str(listing?.pricePrimary);
  if (!pp) return 'Price on request';
  return pp.replace(/\/\s*(month|day|hr|hour).*/i, '').trim() || pp;
}

function txClass(tx) {
  const t = (tx || 'lease').toLowerCase();
  if (t === 'lease' || t === 'lease_to_own') return 'ile-badge--lease';
  if (t === 'rent') return 'ile-badge--rent';
  if (t === 'sale') return 'ile-badge--sale';
  return 'ile-badge--lease';
}

function txLabel(tx) {
  const t = (tx || 'lease').toLowerCase();
  if (t === 'lease' || t === 'lease_to_own') return 'For Lease';
  if (t === 'rent') return 'For Rent';
  if (t === 'sale') return 'For Sale';
  return 'For Lease';
}

function ownershipTag(ot) {
  const t = (ot || '').toLowerCase();
  if (t === 'direct_owner') return 'Owner';
  if (t === 'broker' || t === 'broker_authorized') return 'Broker';
  if (t) return t.replace(/_/g, ' ');
  return 'Lister';
}

export default function InfrastructureLeasingPublicListingPage() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const [sendEnquiryOpen, setSendEnquiryOpen] = useState(false);
  const closeSendEnquiry = useCallback(() => setSendEnquiryOpen(false), []);
  const [viewEnquiryOpen, setViewEnquiryOpen] = useState(false);
  const closeViewEnquiry = useCallback(() => setViewEnquiryOpen(false), []);

  const refetchListing = useCallback(async () => {
    if (!id) return;
    try {
      const res = await fetchPublishedInfrastructureById(id);
      if (res?.success && res?.data) {
        setListing(res.data);
      }
    } catch {
      /* ignore */
    }
  }, [id]);
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [listing, setListing] = useState(null);
  const [error, setError] = useState('');
  const [imgIdx, setImgIdx] = useState(0);
  const [specTab, setSpecTab] = useState('1');
  const [isAuthed] = useState(() => StorageService.isAuthenticated());
  /** Bumps when the session token may have changed, so we refetch and the API can return `viewerIsPublisher`. */
  const [authEpoch, setAuthEpoch] = useState(0);
  const lastTokenRef = useRef(StorageService.getToken() || '');

  useEffect(() => {
    setImgIdx(0);
    setSpecTab('1');
  }, [id]);

  useEffect(() => {
    const t = setInterval(() => {
      const tok = StorageService.getToken() || '';
      if (tok !== lastTokenRef.current) {
        lastTokenRef.current = tok;
        setAuthEpoch((n) => n + 1);
      }
    }, 2000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const onFocusOrVis = () => {
      if (document.visibilityState != null && document.visibilityState !== 'visible') {
        return;
      }
      const tok = StorageService.getToken() || '';
      if (tok !== lastTokenRef.current) {
        lastTokenRef.current = tok;
        setAuthEpoch((n) => n + 1);
      }
    };
    window.addEventListener('focus', onFocusOrVis);
    document.addEventListener('visibilitychange', onFocusOrVis);
    return () => {
      window.removeEventListener('focus', onFocusOrVis);
      document.removeEventListener('visibilitychange', onFocusOrVis);
    };
  }, []);

  useEffect(() => {
    let cancel = false;
    (async () => {
      setError('');
      if (!id) {
        setError('Invalid listing');
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const res = await fetchPublishedInfrastructureById(id);
        if (cancel) {
          return;
        }
        if (res?.success && res?.data) {
          setListing(res.data);
        } else {
          setError(res?.message || 'Could not load this listing.');
        }
      } catch (e) {
        if (!cancel) {
          setError('Could not load this listing.');
        }
      } finally {
        if (!cancel) {
          setLoading(false);
        }
      }
    })();
    return () => {
      cancel = true;
    };
  }, [id, authEpoch]);

  const form = listing?.form;
  const images = useMemo(() => {
    if (!listing) return [];
    const paths = Array.isArray(listing.imagePaths) && listing.imagePaths.length ? listing.imagePaths : [];
    if (paths.length) {
      return paths.map((p) => resolvePublicFileUrl(p));
    }
    if (listing.imagePath) {
      return [resolvePublicFileUrl(listing.imagePath)];
    }
    return [];
  }, [listing]);

  const mainImg = images[imgIdx] || '';

  const depositLine = useMemo(() => {
    if (!form || typeof form !== 'object') return null;
    const d = formatDeposit(form.deposit);
    if (!d) return null;
    return `Deposit: ${d}`;
  }, [form]);

  const sqftLine = useMemo(
    () =>
      perSqftLine(
        listing?.rentAmount,
        form && typeof form === 'object' ? str(form.carpetArea) : null,
      ),
    [listing, form],
  );

  const headerPrice = useMemo(() => {
    if (!listing) {
      return { amount: '—', period: '' };
    }
    const amount = formatHeaderPriceAmount(listing);
    const hasNumeric =
      listing.rentAmount != null && Number.isFinite(listing.rentAmount) && listing.rentAmount >= 0;
    const hasText = str(listing.pricePrimary) && str(listing.pricePrimary) !== 'Price on request';
    const showPeriod = (hasNumeric || hasText) && amount !== '—' && amount !== 'Price on request';
    return {
      amount: amount || 'Price on request',
      period: showPeriod ? pricePeriodLabel(form) : '',
    };
  }, [listing, form]);

  const engagement = useMemo(
    () => (listing ? seedEngagementFromListingId(listing.id) : { views: 0, saves: 0, enquiries: 0 }),
    [listing],
  );

  const availabilityHeadline = useMemo(
    () => (listing ? getListingAvailabilityHeadline(listing) : null),
    [listing],
  );
  const availabilityLineBullets = useMemo(
    () => (listing ? getInventoryLineSummaryBullets(listing) : []),
    [listing],
  );
  const bookedRangeLines = useMemo(() => {
    const ia = listing?.inventoryAvailability;
    if (!ia?.bookedRanges?.length) return [];
    return formatBookedRangesSummaryLines(ia.bookedRanges, 5);
  }, [listing]);
  const nextCapacityHint = useMemo(() => {
    const ia = listing?.inventoryAvailability;
    if (!ia?.bookedRanges?.length) return null;
    return estimateEarliestCapacityAfterBookings(ia.bookedRanges);
  }, [listing]);

  const listingsHref = useMemo(() => {
    const tab = searchParams.get('tab') || (listing?.listingType === 'machinery' ? 'equipment' : 'real-estate');
    return `/infrastructure-leasing/listings?tab=${tab === 'equipment' ? 'equipment' : 'real-estate'}`;
  }, [searchParams, listing?.listingType]);

  const isViewerThePublisher = listing?.viewerIsPublisher === true;
  const enquiryAlreadySent = listing?.viewerHasSubmittedEnquiry === true;

  const onShare = () => {
    const url = typeof window !== 'undefined' ? window.location.href : '';
    if (navigator.share) {
      navigator
        .share({ title: listing?.title, url })
        .catch(() => {
          if (url) {
            try {
              navigator.clipboard.writeText(url);
            } catch {
              /* ignore */
            }
          }
        });
    } else if (url) {
      try {
        navigator.clipboard.writeText(url);
      } catch {
        /* ignore */
      }
    }
  };

  return (
    <div className="ile-public-detail">
      <WebsiteTopBar />
      <div className="ile-public-detail__subnav">
        <div className="container">
          <div className="ile-public-detail__subnav-inner">
            <div className="ile-public-detail__subnav-links">
              <Link to={listingsHref} className="ile-public-detail__link-light">
                ← Back to search
              </Link>
              <span className="text-muted" style={{ userSelect: 'none' }}>
                |
              </span>
              <Link
                to={`/infrastructure-leasing/post-requirement?listingId=${encodeURIComponent(id)}&new=1`}
                className="ile-public-detail__link-light"
              >
                Post requirement
              </Link>
            </div>
            <div className="d-flex align-items-center gap-2">
              {isAuthed ? (
                <span className="text-secondary small d-none d-sm-inline">You are signed in</span>
              ) : (
                <Link to="/login" className="ile-public-detail__link-light">
                  Sign in
                </Link>
              )}
              <div className="ile-public-detail__list-cta">
                <Link to="/infrastructure-leasing/list-asset" className="btn btn-dark">
                  List your asset
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="container py-4 ile-public-detail__main">
        <button
          type="button"
          className="btn btn-link px-0 text-decoration-none text-secondary d-lg-none mb-2"
          onClick={() => navigate(-1)}
        >
          ← Back
        </button>
        {loading && <p className="text-secondary">Loading…</p>}
        {error && !loading && <p className="text-danger mb-0">{error}</p>}
        {listing && !loading && !error && (
          <div className="row g-4">
            <div className="col-lg-8 order-1">
              <div className="ile-hero">
                {mainImg ? (
                  <img
                    src={mainImg}
                    alt=""
                    onError={(e) => {
                      e.currentTarget.style.visibility = 'hidden';
                    }}
                  />
                ) : (
                  <div
                    className="d-flex align-items-center justify-content-center text-secondary"
                    style={{ minHeight: 220 }}
                  >
                    No image
                  </div>
                )}
                <div className="ile-hero__badges">
                  <div className="ile-badge ile-badge--ok">
                    <FaShieldAlt style={{ fontSize: '0.7rem' }} />
                    {form && typeof form === 'object' && str(form.ownershipType) === 'direct_owner' ? 'Verified owner' : 'Active listing'}
                  </div>
                  <div className={`ile-badge ${txClass(listing.transactionType)}`}>{txLabel(listing.transactionType)}</div>
                </div>
              </div>

              {images.length > 1 && (
                <div className="ile-thumb-row" role="list" aria-label="More photos">
                  {images.map((src, i) => (
                    <button
                      type="button"
                      key={src + i}
                      onClick={() => setImgIdx(i)}
                      style={
                        i === imgIdx
                          ? { borderColor: '#ff6a00', boxShadow: '0 0 0 1px #ff6a00' }
                          : undefined
                      }
                    >
                      <img src={src} alt="" />
                    </button>
                  ))}
                </div>
              )}

              <div className="ile-listing-summary-card">
                <h1 className="ile-listing-title">{str(listing.title) || 'Listing'}</h1>
                <div className="ile-header-meta">
                  {str(listing.locationLine) && listing.locationLine !== '—' && (
                    <div className="ile-loc">
                      <FaMapMarkerAlt className="ile-loc__icon" aria-hidden />
                      <span>{listing.locationLine}</span>
                    </div>
                  )}
                  <div className="ile-tags ile-tags--header" role="list">
                    {str(listing.categoryLabel) && (
                      <span className="ile-tag" role="listitem">
                        {str(listing.categoryLabel)}
                      </span>
                    )}
                    {!str(listing.categoryLabel) &&
                      str(listing.propertyType) &&
                      PROPERTY_TYPE_LABELS[listing.propertyType] && (
                        <span className="ile-tag" role="listitem">
                          {PROPERTY_TYPE_LABELS[listing.propertyType]}
                        </span>
                      )}
                    {str(ownershipTag(listing.ownershipType)) && (
                      <span className="ile-tag" role="listitem">
                        {ownershipTag(listing.ownershipType)}
                      </span>
                    )}
                    {availabilityHeadline && (
                      <span
                        className={`ile-tag ile-avail-pill ${availabilityHeadline.badgeClass}`}
                        role="listitem"
                        title={availabilityHeadline.sub || undefined}
                      >
                        {availabilityHeadline.headline}
                      </span>
                    )}
                  </div>
                </div>

                {(str(listing.countryName) || str(listing.stateName) || str(listing.cityName)) && (
                  <div className="ile-geo-row" aria-label="Country, state, and city">
                    {str(listing.countryName) && (
                      <span className="ile-geo-part">
                        <span className="ile-geo-key">Country</span>
                        {str(listing.countryName)}
                      </span>
                    )}
                    {str(listing.stateName) && (
                      <span className="ile-geo-part">
                        <span className="ile-geo-key">State</span>
                        {str(listing.stateName)}
                      </span>
                    )}
                    {str(listing.cityName) && (
                      <span className="ile-geo-part">
                        <span className="ile-geo-key">City</span>
                        {str(listing.cityName)}
                      </span>
                    )}
                  </div>
                )}

                <div className="ile-price-card">
                  <div className="ile-price-primary-row">
                    <span className="ile-price-amount">{headerPrice.amount}</span>
                    {headerPrice.period && <span className="ile-price-period"> {headerPrice.period}</span>}
                  </div>
                  {depositLine && <div className="ile-price-line-secondary">{depositLine}</div>}
                  {sqftLine && <div className="ile-price-line-secondary">{sqftLine}</div>}
                </div>

                <div
                  className="ile-engagement-row"
                  aria-label="Listing engagement (illustrative until analytics are connected)"
                >
                  <div className="ile-engagement-item">
                    <FaUserFriends className="ile-engagement-icon" aria-hidden />
                    <span>{engagement.views.toLocaleString('en-IN')} views</span>
                  </div>
                  <div className="ile-engagement-item">
                    <FaRegHeart className="ile-engagement-icon" aria-hidden />
                    <span>{engagement.saves.toLocaleString('en-IN')} saves</span>
                  </div>
                  <div className="ile-engagement-item">
                    <FaRegCommentAlt className="ile-engagement-icon" aria-hidden />
                    <span>{engagement.enquiries.toLocaleString('en-IN')} enquiries</span>
                  </div>
                </div>
              </div>

              {listing.inventoryAvailability && listing.inventoryAvailability.listingAvailabilityState !== 'not_tracked' && (
                <div className="ile-avail-panel">
                  <div className="ile-avail-panel__head">
                    <h2 className="ile-avail-panel__title">Availability</h2>
                    {availabilityHeadline && (
                      <span className={`ile-avail-pill ${availabilityHeadline.badgeClass}`}>
                        {availabilityHeadline.headline}
                      </span>
                    )}
                  </div>
                  {availabilityHeadline?.sub && (
                    <p className="ile-avail-panel__sub mb-2">{availabilityHeadline.sub}</p>
                  )}
                  {listing.inventoryAvailability.inventoryManaged && availabilityLineBullets.length > 0 && (
                    <ul className="ile-avail-panel__list mb-2">
                      {availabilityLineBullets.map((line) => (
                        <li key={line}>{line}</li>
                      ))}
                    </ul>
                  )}
                  {!listing.inventoryAvailability.inventoryManaged && (
                    <p className="small text-muted mb-2">
                      Inventory totals are not tracked on this listing; booked periods below reflect agreements and pipeline holds where applicable.
                    </p>
                  )}
                  {bookedRangeLines.length > 0 && (
                    <div className="ile-avail-panel__booked">
                      <span className="ile-avail-panel__label">Committed periods (summary)</span>
                      <ul className="ile-avail-panel__list mb-0">
                        {bookedRangeLines.map((line) => (
                          <li key={line} className="small">
                            {line}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {nextCapacityHint && (
                    <p className="small text-muted mt-2 mb-0">
                      After the latest committed window, capacity may open from{' '}
                      <strong>{nextCapacityHint}</strong> onward — confirm dates with the lister before relying on this for planning.
                    </p>
                  )}
                </div>
              )}

              <div className="ile-spec-card">
                <h2 className="ile-spec-title">Listing details</h2>
                <div className="ile-spec-tabs ile-spec-tabs--six" role="tablist" aria-label="Listing detail sections">
                  {PUBLIC_SPEC_TAB_IDS.map((k) => (
                    <button
                      key={k}
                      type="button"
                      role="tab"
                      className={`ile-spec-tab${specTab === k ? ' active' : ''}`}
                      onClick={() => setSpecTab(k)}
                      id={`ile-spec-${k}`}
                    >
                      {getPublicSpecTabLabel(k, listing)}
                    </button>
                  ))}
                </div>
                <div role="tabpanel" aria-labelledby={`ile-spec-${specTab}`}>
                  <PublicListingSpecTabContent
                    specTab={specTab}
                    form={form}
                    listing={listing}
                    txLabel={txLabel}
                  />
                </div>
              </div>
            </div>

            <div className="col-lg-4 order-2">
              <div className="ile-side-card sticky-lg-top" style={{ top: 20 }}>
                <h2 className="ile-side-title">Contact seller</h2>
                {isViewerThePublisher ? (
                  <p className="ile-side-sub">This is your listing — enquiries are for other buyers only.</p>
                ) : (
                  <p className="ile-side-sub">Get in touch to discuss this listing.</p>
                )}
                {!isViewerThePublisher && enquiryAlreadySent && (
                  <div className="d-flex flex-column gap-2 mb-2">
                    <div
                      className="d-flex align-items-center gap-2 small fw-semibold py-2 px-3 rounded border border-success border-opacity-25 text-success"
                      style={{ background: 'rgba(25, 135, 84, 0.08)' }}
                      role="status"
                    >
                      <FaCheck aria-hidden />
                      Enquiry already sent
                    </div>
                    <button
                      type="button"
                      className="ile-btn-cta ile-btn-cta--outline w-100"
                      onClick={() => setViewEnquiryOpen(true)}
                    >
                      <FaEye className="me-1" aria-hidden />
                      View enquiry
                    </button>
                  </div>
                )}
                {!isViewerThePublisher && !enquiryAlreadySent && (
                  <button
                    type="button"
                    className="ile-btn-cta ile-btn-cta--dark border-0 w-100"
                    onClick={() => {
                      if (!StorageService.isAuthenticated()) {
                        navigate('/login', {
                          state: { from: `${location.pathname}${location.search || ''}` },
                        });
                        return;
                      }
                      setSendEnquiryOpen(true);
                    }}
                  >
                    <FaEnvelope /> Send enquiry
                  </button>
                )}
                <Link to={isAuthed ? '/home' : '/login'} className="ile-btn-cta ile-btn-cta--outline text-decoration-none">
                  <FaPhone style={{ fontSize: '0.9rem' }} />
                  Request callback
                </Link>
                <button type="button" className="ile-btn-cta ile-btn-cta--outline" disabled title="Available soon">
                  <FaCalendarAlt style={{ fontSize: '0.9rem' }} />
                  Schedule site visit
                </button>
                <button type="button" className="ile-btn-cta ile-btn-cta--outline" disabled title="Add brochure from lister account">
                  <FaDownload style={{ fontSize: '0.9rem' }} />
                  Download brochure
                </button>
                <div className="ile-side-actions">
                  <button type="button" className="btn btn-outline-secondary btn-sm" onClick={onShare}>
                    <FaShare className="me-1" />
                    Share
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm"
                    onClick={() => {
                      if (isAuthed) {
                        void 0; /* future: save to favourites */
                      } else {
                        navigate('/login');
                      }
                    }}
                    title="Sign in to save"
                  >
                    <FaRegHeart className="me-1" />
                    Save
                  </button>
                </div>
                <Link to={listingsHref} className="ile-btn-cta ile-btn-cta--muted text-decoration-none">
                  <FaCopy className="opacity-75" />
                  Compare with other listings
                </Link>
              </div>

              <div className="ile-side-card">
                <h2 className="ile-side-title">Seller profile</h2>
                {!listing.publisher ? (
                  <p className="text-secondary small mb-2">Published on Trocanet (publisher details loading or unavailable).</p>
                ) : listing.publisher.isCompanyVerified ? (
                  <div className="d-flex align-items-center gap-2 text-success small fw-bold mb-2" style={{ fontSize: '0.8rem' }}>
                    <FaCheck /> Verified company on Trocanet
                  </div>
                ) : listing.publisher.isGuest ? (
                  <p className="text-secondary small mb-2">Guest listing (not linked to a signed-in business profile).</p>
                ) : (
                  <p className="text-secondary small mb-2">Member account — use enquiry to reach the lister.</p>
                )}
                <div className="ile-seller">
                  <div className="ile-seller-av" aria-hidden>
                    <FaUser />
                  </div>
                  <div>
                    <p className="ile-seller-name">
                      {str(listing.publisher?.displayName) || 'Publisher'}
                    </p>
                    <p className="ile-seller-role">
                      {str(listing.publisher?.subtitle) ||
                        'Published this asset on the Infrastructure & Asset Exchange marketplace.'}
                    </p>
                  </div>
                </div>
                <p className="text-secondary small mt-1 mb-1">
                  {listing.listingType === 'property' && str(listing.propertyType)
                    ? PROPERTY_TYPE_LABELS[listing.propertyType] || str(listing.propertyType)
                    : str(listing.categoryLabel) || 'Listing'}
                </p>
                {str(ownershipTag(listing.ownershipType)) && str(ownershipTag(listing.ownershipType)) !== 'Lister' && (
                  <p className="text-secondary small mb-0">
                    <span className="text-muted">Role on listing: </span>
                    {ownershipTag(listing.ownershipType)}
                  </p>
                )}
                <ul className="ile-seller-stat">
                  <li>Listing updated {listing.updatedAt ? new Date(listing.updatedAt).toLocaleDateString('en-IN') : '—'}</li>
                  <li>Contact the publisher to discuss availability and viewings</li>
                </ul>
                <div className="text-center">
                  <Link to={listingsHref} className="small text-decoration-none" style={{ color: '#ff6a00' }}>
                    View all listings
                  </Link>
                </div>
              </div>

              <div className="ile-safety-card">
                <h3 className="ile-safety-title">
                  <span className="rounded-circle bg-white d-inline-flex p-1 me-1" style={{ color: '#ff6a00' }} aria-hidden>
                    ·
                  </span>
                  Safety tips
                </h3>
                <ul>
                  <li>Verify property documents, ownership, and identity before you pay.</li>
                  <li>Visit the site in person; do not accept inspection waivers for large commitments.</li>
                  <li>Prefer platform messaging and recorded contact where available.</li>
                  <li>Be cautious of requests to pay outside a formal agreement or invoice.</li>
                </ul>
              </div>
            </div>
          </div>
        )}
      </div>

      {listing && !error && (
        <>
          <InfrastructureLeasingSendEnquiryModal
            open={sendEnquiryOpen}
            onClose={closeSendEnquiry}
            listing={listing}
            listingId={id}
            onEnquirySent={refetchListing}
          />
          <InfrastructureLeasingViewEnquiryModal open={viewEnquiryOpen} onClose={closeViewEnquiry} listingId={id} />
        </>
      )}
    </div>
  );
}
